/**
 * Transient feedback.
 *
 * Toasts are for things that already happened. Anything that needs a decision
 * gets a sheet; anything that needs to persist gets a state on the page. The
 * distinction matters because a toast is, by design, missable.
 *
 * Errors here always carry an action where one exists — "Couldn't publish ·
 * Retry" is useful, "Something went wrong" is not.
 */

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface ToastAction {
  label: string;
  onPress: () => void;
}

export interface Toast {
  id: number;
  message: string;
  tone: 'neutral' | 'danger';
  action?: ToastAction;
}

interface ToastApi {
  show: (message: string, options?: { tone?: Toast['tone']; action?: ToastAction; duration?: number }) => void;
  /** Convenience for the overwhelmingly common failure case. */
  error: (message: string, retry?: () => void) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  const show = useCallback<ToastApi['show']>(
    (message, options) => {
      const id = nextId.current++;
      const toast: Toast = {
        id,
        message,
        tone: options?.tone ?? 'neutral',
        ...(options?.action ? { action: options.action } : {}),
      };
      // Three at a time. A stack taller than that covers the content the user
      // is trying to look at.
      setToasts((current) => [...current.slice(-2), toast]);

      // A toast with an action stays longer — the reader has to notice it,
      // read it, and decide.
      const duration = options?.duration ?? (options?.action ? 8000 : 4000);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), duration)
      );
    },
    [dismiss]
  );

  const error = useCallback<ToastApi['error']>(
    (message, retry) => {
      show(message, {
        tone: 'danger',
        ...(retry ? { action: { label: 'Retry', onPress: retry } } : {}),
      });
    },
    [show]
  );

  const api = useMemo(() => ({ show, error, dismiss }), [show, error, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/*
        aria-live=polite rather than assertive: these announce completed work,
        and interrupting a screen-reader mid-sentence to say "Saved" is rude.
      */}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast${toast.tone === 'danger' ? ' toast--danger' : ''}`}>
            <span>{toast.message}</span>
            {toast.action ? (
              <button
                type="button"
                onClick={() => {
                  dismiss(toast.id);
                  toast.action?.onPress();
                }}
              >
                {toast.action.label}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
